import {
	MutationCache,
	QueryCache,
	QueryClient,
	hashKey,
	type QueryCacheNotifyEvent
} from '@tanstack/svelte-query';
import { browser } from '$app/environment';
import {
	getGetCurrentUserInfoApiUsersMeGetQueryKey,
	getGetCurrentUserInfoApiUsersMeGetQueryOptions
} from '$lib/api/generated/users/users';
import type { UserRead } from '$lib/api/generated/schemas';
import { setHealthConsentRequiredListener } from '$lib/api/orval/mutator';
import {
	handleHealthConsentRequiredError,
	isConsentLoss,
	shouldRetryQuery
} from './healthConsentErrors';

let queryClient: QueryClient;

/**
 * The full tenant reset lives in auth.ts, which imports this module; auth.ts
 * registers it here instead of this module importing auth.ts back. Until then
 * (and in isolated tests) dropping the query cache is the fallback.
 * `clearPersistedTenantContent` drops only what survives a reload (drafts in
 * browser storage) and is safe to repeat.
 */
let resetTenantContent: () => void = () => {
	queryClient?.clear();
	forgetCurrentUserConsent();
};
let clearPersistedTenantContent: () => void = () => {};

export function registerTenantContentReset(
	reset: () => void,
	clearPersisted: () => void = () => {}
): void {
	resetTenantContent = reset;
	clearPersistedTenantContent = clearPersisted;
}

/**
 * After a reset the user query is gone, so it is fetched again; otherwise it
 * is invalidated. `cancelRefetch: false` keeps a burst of parallel 403s to one
 * /me request.
 */
function refreshCurrentUser(): void {
	const queryKey = getGetCurrentUserInfoApiUsersMeGetQueryKey();
	if (queryClient.getQueryState(queryKey)) {
		void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
	} else {
		void queryClient.prefetchQuery(getGetCurrentUserInfoApiUsersMeGetQueryOptions());
	}
}

/**
 * SML-140: a 403 HEALTH_CONSENT_REQUIRED anywhere drops the tenant content of
 * this tab when consent was withdrawn elsewhere, and refreshes the current
 * user, so the layout guards route to the consent screen. The API mutator
 * reports every such 403 (also from direct store calls); the cache handlers
 * cover errors that never went through it. A repeated report only refreshes.
 */
function onApiError(error: unknown): void {
	if (!queryClient) return;
	handleHealthConsentRequiredError(error, {
		cachedConsent: () =>
			queryClient.getQueryData<UserRead>(getGetCurrentUserInfoApiUsersMeGetQueryKey())
				?.health_data_consent,
		resetTenantContent: () => resetTenantContent(),
		refreshCurrentUser
	});
}

let lastSeenConsentStatus: NonNullable<UserRead['health_data_consent']>['status'] | undefined;

/**
 * Called by every tenant reset in auth.ts: the next user value has no
 * predecessor. A gc removal of an unobserved /me does not forget it, so a
 * withdrawal seen after a long absence from the guarded pages still resets.
 */
export function forgetCurrentUserConsent(): void {
	lastSeenConsentStatus = undefined;
}

/**
 * SML-140: the current user can report the withdrawal before any content
 * request does (a focus refetch of /me lands first, and the content 403s that
 * follow already see `withdrawn`). The granted → not granted transition of the
 * cached user drops the tenant content too. The reset forgets the last seen
 * status, so the next value has no granted predecessor and cannot trigger it
 * again. Without a granted predecessor (a reload or a new tab after the
 * withdrawal) nothing was loaded in memory, but a draft kept in browser storage
 * would come back after re-consent, so any non-granted user drops those.
 */
function watchCurrentUserConsent(event: QueryCacheNotifyEvent): void {
	if (event.type !== 'updated') return;
	if (event.query.queryHash !== hashKey(getGetCurrentUserInfoApiUsersMeGetQueryKey())) return;
	const next = (event.query.state.data as UserRead | undefined)?.health_data_consent?.status;
	if (next === undefined) return;
	const previous = lastSeenConsentStatus;
	lastSeenConsentStatus = next;
	if (isConsentLoss(previous, next)) resetTenantContent();
	else if (next !== 'granted') clearPersistedTenantContent();
}

export function getQueryClient(): QueryClient {
	if (!queryClient) {
		queryClient = new QueryClient({
			queryCache: new QueryCache({ onError: onApiError }),
			mutationCache: new MutationCache({ onError: onApiError }),
			defaultOptions: {
				queries: {
					// Default query options
					staleTime: 1000 * 60 * 5, // 5 minutes
					gcTime: 1000 * 60 * 10, // 10 minutes (was cacheTime)
					// Up to 3 retries, except for HEALTH_CONSENT_REQUIRED (SML-140).
					retry: shouldRetryQuery,
					refetchOnWindowFocus: true,
					// Only run queries on browser side by default
					enabled: browser
				},
				mutations: {
					// Default mutation options
					retry: false
				}
			}
		});
		queryClient.getQueryCache().subscribe(watchCurrentUserConsent);
		// Browser only: on the server this module is shared between requests.
		if (browser) setHealthConsentRequiredListener(onApiError);
	}
	return queryClient;
}
